/** Main navigation bar: user menu, logout, nav links; responsive with a
 * mobile menu. */

import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  FilterAlt,
  Home,
  AccountBalance,
  AdminPanelSettings,
  CloudSync,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import ConfirmDialog from '../Common/ConfirmDialog';
import LanguageSwitcher from './LanguageSwitcher';

interface NavigationItem {
  label: string;
  path: string;
  icon: ReactNode;
}

/**
 * AppBar Component
 */
const AppBar = (): ReactNode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const { user, logout, isAuthenticated, isAdmin } = useAuth();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleUserMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleUserMenuClose();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    setLogoutDialogOpen(false);
    await logout();
    void navigate('/login');
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigate = (path: string) => {
    void navigate(path);
    setMobileMenuOpen(false);
  };

  // Navigation items
  const navigationItems: NavigationItem[] = [
    { label: t('nav.ads'), path: '/ads', icon: <Home /> },
    { label: t('nav.criteria'), path: '/criteria', icon: <FilterAlt /> },
    { label: t('nav.mortgage'), path: '/mortgage', icon: <AccountBalance /> },
    { label: t('nav.scraping'), path: '/scraping', icon: <CloudSync /> },
  ];

  if (isAdmin) {
    navigationItems.push({
      label: t('nav.administration'),
      path: '/admin',
      icon: <AdminPanelSettings />,
    });
  }

  if (!isAuthenticated) {
    return null; // Don't show AppBar on login page
  }

  return (
    <>
      <MuiAppBar position="sticky">
        <Toolbar>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label={t('aria.menu')}
              onClick={handleMobileMenuToggle}
              sx={{ mr: 2, minWidth: 44, minHeight: 44 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo/Title */}
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: isMobile ? 1 : 0, cursor: 'pointer' }}
            onClick={() => {
              void navigate('/ads');
            }}
          >
            {t('appTitle')}
          </Typography>

          {/* Desktop navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', ml: 4, gap: 1 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  startIcon={item.icon}
                  onClick={() => {
                    void navigate(item.path);
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* User menu */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <LanguageSwitcher />
            <IconButton
              size="large"
              aria-label={t('aria.accountMenu')}
              aria-controls="user-menu"
              aria-haspopup="true"
              onClick={handleUserMenuOpen}
              color="inherit"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              {user?.username ? (
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {user.username[0]?.toUpperCase()}
                </Avatar>
              ) : (
                <AccountCircle />
              )}
            </IconButton>
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem disabled>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {user?.username}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {user?.email}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'text.secondary',
                    }}
                  >
                    {t('userMenu.role', { role: user?.role })}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogoutClick} sx={{ color: 'error.main' }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                {t('userMenu.logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </MuiAppBar>

      {/* Mobile drawer */}
      <Drawer anchor="left" open={mobileMenuOpen} onClose={handleMobileMenuToggle}>
        <Box sx={{ width: 250 }} role="presentation">
          <List>
            {navigationItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleNavigate(item.path);
                  }}
                >
                  {item.icon}
                  <ListItemText primary={item.label} sx={{ ml: 2 }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Logout confirmation dialog */}
      <ConfirmDialog
        open={logoutDialogOpen}
        title={t('logoutDialog.title')}
        message={t('logoutDialog.message')}
        onConfirm={() => {
          void handleLogoutConfirm();
        }}
        onCancel={handleLogoutCancel}
        confirmText={t('logoutDialog.confirm')}
        confirmColor="primary"
      />
    </>
  );
};

export default AppBar;
